import { Clock3, RotateCcw, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { DocumentRecord, RevisionRecord } from '../../shared/types'
import { ApiClientError, apiFetch } from '../api'

export function RevisionPanel({ document, onRestored }: { document: DocumentRecord; onRestored: (document: DocumentRecord) => void }) {
  const [revisions, setRevisions] = useState<RevisionRecord[]>([])
  const [preview, setPreview] = useState<RevisionRecord | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    apiFetch<RevisionRecord[]>(`/api/documents/${document.id}/revisions`)
      .then((items) => { if (active) setRevisions(items) })
      .catch((reason) => { if (active) setError(reason instanceof ApiClientError ? reason.message : '版本记录加载失败') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [document.id, document.version])

  async function restore() {
    if (!preview) return
    try {
      const restored = await apiFetch<DocumentRecord>(`/api/documents/${document.id}/restore`, {
        method: 'POST',
        body: JSON.stringify({ revisionVersion: preview.version, version: document.version }),
      })
      onRestored(restored)
      setPreview(null)
      setConfirming(false)
    } catch (reason) {
      setError(reason instanceof ApiClientError ? reason.message : '恢复失败，请重试')
      setConfirming(false)
    }
  }

  return (
    <aside className="side-panel revision-panel" aria-label="版本记录面板">
      <div className="panel-heading"><div><p className="eyebrow">HISTORY</p><h2>版本记录</h2></div><span>{revisions.length}</span></div>
      {error ? <p className="panel-error" role="alert">{error}</p> : null}
      {loading ? <p className="muted">正在翻阅记录…</p> : null}
      <div className="revision-list">
        {revisions.map((revision) => (
          <button key={revision.id} type="button" onClick={() => setPreview(revision)} aria-label={`查看版本 ${revision.version}`} className={preview?.id === revision.id ? 'active' : ''}>
            <span className="revision-dot" /><span><strong>版本 {revision.version}</strong><small><Clock3 size={12} />{revision.actor} · {new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(revision.createdAt))}</small></span><em>{revision.source === 'ai' ? 'AI' : revision.source === 'restore' ? '恢复' : '编辑'}</em>
          </button>
        ))}
      </div>
      {preview ? <div className="revision-preview"><button className="preview-close" type="button" onClick={() => setPreview(null)} aria-label="关闭版本预览"><X size={16} /></button><p className="eyebrow">VERSION {preview.version}</p><h3>{preview.title}</h3><pre>{preview.content || '（空白文档）'}</pre><button className="secondary-button" type="button" onClick={() => setConfirming(true)}><RotateCcw size={16} />恢复这个版本</button></div> : null}
      {confirming && preview ? <div className="modal-backdrop panel-modal"><section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="restore-heading"><h2 id="restore-heading">恢复历史版本？</h2><p>当前内容不会丢失；系统会把版本 {preview.version} 保存为一个新的版本。</p><div className="modal-actions"><button className="ghost-button" type="button" onClick={() => setConfirming(false)}>取消</button><button className="primary-button" type="button" onClick={() => void restore()}>确认恢复</button></div></section></div> : null}
    </aside>
  )
}
